import {useEffect, useState} from "@wordpress/element";
import {
    Container, Stack,
    List, ListItemButton,
    CircularProgress, Typography, useMediaQuery, useTheme, Button,
} from '@mui/material';
import {FixedSizeList} from 'react-window';
import {SpacesControls} from "../../common/SpacesControls";
import {
    formatAvailType,
    formatSuite,
    isEmpty,
    maybeImgLinkToSpace,
    maybeLinkToSpace,
    applyState,
    toParam
} from "../../common/util";
import {addQueryArgs} from "@wordpress/url";
import apiFetch from "@wordpress/api-fetch";

function SpaceListItem({space, height, onHover}) {
    if (space === undefined) {
        return <div>undefined</div>;
    }
    const spaceID = space['Space_ID'];
    const title = `${space['PropertyName']}${formatSuite(space['Suite'])}`;
    let maybeGoToSpace = null;
    if (space['Space_ID'] !== null && space['Space_ID'] !== '') {
        maybeGoToSpace = (_) => {
            if (space['Space_ID']) {
                const spaceDetailURL = `${window.location.origin}/space/?id=${space['Space_ID']}`;
                window.location.assign(spaceDetailURL);
            }
        };
    }
    let style = {width: '100%', height: '100%'};
    if (height) {
        style.height = `${height}px`;
        style.paddingTop = `4px`;
        style.paddingBottom = `4px`;
    }
    let fontScale = '1.6vw';
    if (useMediaQuery(useTheme().breakpoints.up('lg'))) {
        fontScale = '1.1vw';
    }
    return (
        <ListItemButton key={`spaces-list-item-button-${spaceID}`} disableGutters divider={true}
                        disabled={maybeGoToSpace == null}
                        onClick={maybeGoToSpace}
                        onMouseOver={() => onHover?.(space)}
                        style={style}>
            <Container key={`spaces-list-item-container-${spaceID}`} disableGutters
                       sx={{display: 'flex', flexDirection: 'row', alignContent: 'start', ...style}}>
                <Container key={`spaces-list-item-img-link-container-${spaceID}`}
                           disableGutters sx={{minHeight: '100%', flexShrink: 2}}>
                    {maybeImgLinkToSpace(space, title)}
                </Container>
                <Container key={`spaces-list-item-content-container-${spaceID}`}
                           disableGutters sx={{
                    minHeight: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    paddingLeft: '5px',
                    paddingBottom: '1px',
                }}>
                    {maybeLinkToSpace(space, title)}
                    <Container disableGutters sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                    }}>
                        <Typography variant='body2'
                                    sx={{fontSize: `clamp(14px, ${fontScale}, 1.1rem)`}}>
                            +/- {Number(space['AvailableSqft']).toLocaleString()} RSF
                        </Typography>
                        <Typography variant='body2'
                                    sx={{fontSize: `clamp(14px, ${fontScale}, 1.1rem)`}}>
                            {formatAvailType(space['AvailType'])}{space['LeaseType']}
                        </Typography>
                    </Container>
                </Container>
            </Container>
        </ListItemButton>
    );
}

function SpacesList({showControls = true, scrollable = false, onSpaceHover, onSpacesChanged, ...attributes}) {
    const [city, setCity] = useState(toParam(attributes.city));
    const [propertyType, setPropertyType] = useState(toParam(attributes.propertyType));
    const [subType, setSubType] = useState(toParam(attributes.subType));
    const [availType, setAvailType] = useState(toParam(attributes.availType));
    const [minSqft, setMinSqft] = useState(toParam(attributes.minSqft));
    const [maxSqft, setMaxSqft] = useState(toParam(attributes.maxSqft));
    const [minRent, setMinRent] = useState(toParam(attributes.minRent));
    const [maxRent, setMaxRent] = useState(toParam(attributes.maxRent));
    const [spaces, setSpaces] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingAll, setLoadingAll] = useState(true);
    const [hasMore, setHasMore] = useState(false);
    const [page, setPage] = useState(0);
    const [error, setError] = useState(null);
    const pageSize = scrollable ? 1000 : 10;

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        console.log('SpacesList attributes changed', attributes, params);
        applyState(setCity, params, 'city', attributes.city);
        applyState(setPropertyType, params, 'propertyType', attributes.propertyType);
        applyState(setSubType, params, 'subType', attributes.subType);
        applyState(setAvailType, params, 'availType', attributes.availType);
        applyState(setMinSqft, params, 'minSqft', attributes.minSqft);
        applyState(setMaxSqft, params, 'maxSqft', attributes.maxSqft);
        applyState(setMinSqft, params, 'minRent', attributes.minRent);
        applyState(setMaxSqft, params, 'maxRent', attributes.maxRent);
    }, [attributes.city, attributes.propertyType, attributes.subType, attributes.availType,
        attributes.minSqft, attributes.maxSqft, attributes.minRent, attributes.maxRent]);

    const fetch = () => {
        let urlPath = addQueryArgs('/ebo-spaces/v1/spaces', {
            city: isEmpty(city) ? null : city,
            type: isEmpty(propertyType) ? null : propertyType,
            subType: isEmpty(subType) ? null : subType,
            availType: isEmpty(availType) ? null : availType,
            minSqft: isEmpty(minSqft) ? null : minSqft,
            maxSqft: isEmpty(maxSqft) ? null : maxSqft,
            minRent: isEmpty(minRent) ? null : minRent,
            maxRent: isEmpty(maxRent) ? null : maxRent,
            offset: page * pageSize,
            limit: pageSize,
        });
        setLoading(true);
        setLoadingAll(page === 0);
        apiFetch({path: urlPath})
            .then((result) => {
                let newSpaces = result['records'];
                let hasMore = result['has_more'];

                if (page === 0) {
                    setSpaces(newSpaces);
                } else {
                    setSpaces((spaces) => [...spaces, ...newSpaces]);
                }
                setHasMore(hasMore);
                setLoading(false);
                setLoadingAll(false);
            })
            .catch((error) => {
                setError(error);
                setLoading(false);
                setLoadingAll(false);
            });
    };

    useEffect(() => {
        console.log('SpacesList filters changed, fetching',
            city, propertyType, subType, availType, minSqft, maxSqft, minRent, maxRent, pageSize);
        setPage(0);
        fetch();
    }, [city, propertyType, subType, availType, minSqft, maxSqft, minRent, maxRent, pageSize]);

    useEffect(() => {
        fetch();
    }, [page]);

    useEffect(() => {
        onSpacesChanged?.(spaces);
    }, [spaces]);

    if (error) {
        return <div>Error: {error.message}</div>
    } else {
        return <>
            <Stack key='spaces-list-stack' spacing={2}
                   sx={{width: '100%', minWidth: '320px', alignContent: 'center', justifyContent: 'center'}}>
                {showControls && (
                    <SpacesControls key='spaces-list-controls'
                                    city={city}
                                    onCityChange={setCity}
                                    propertyType={propertyType}
                                    onTypeChange={setPropertyType}
                                    subType={subType}
                                    onSubTypeChange={setSubType}
                                    availType={availType}
                                    onAvailTypeChange={setAvailType}
                                    minSqft={minSqft}
                                    maxSqft={maxSqft}
                                    onSqftChange={(range) => {
                                        setMinSqft(range[0]);
                                        setMaxSqft(range[1]);
                                    }}
                                    minRent={minRent}
                                    maxRent={maxRent}
                                    onRentChange={(range) => {
                                        setMinRent(range[0]);
                                        setMaxRent(range[1]);
                                    }}
                                    showControls={showControls}
                                    onError={setError}/>
                )}
                {loading && (scrollable || loadingAll) && (
                    <Stack direction='row' sx={{alignContent: 'center', justifyContent: 'center'}}>
                        <ListItemButton key='loading-spinner' sx={{
                            width: '100%',
                            height: '100%',
                            justifyContent: 'center',
                            alignContent: 'center'
                        }}>
                            <CircularProgress/>
                        </ListItemButton>
                    </Stack>
                )}
                {!loading && scrollable && (
                    <FixedSizeList key='spaces-list-fixed-size'
                                   height={500}
                                   itemSize={160}
                                   itemCount={spaces.length}
                                   style={{width: '100%', minWidth: '320px'}}>
                        {({index, style}) => {
                            return (
                                <Container disableGutters style={{width: '100%', minWidth: '320px', ...style}}>
                                    <SpaceListItem key={`spaces-list-item-${index}`} space={spaces[index]} height={160}
                                                   onHover={onSpaceHover}/>
                                </Container>
                            );
                        }}
                    </FixedSizeList>
                )}
                {!loadingAll && !scrollable && (
                    <List key='spaces-list' component='nav'
                          sx={{width: '100%', minWidth: '320px', bgcolor: 'background.paper'}}>
                        {spaces.map((space, index) => {
                            return <SpaceListItem key={`spaces-list-item-${index}`}
                                                  space={space}
                                                  onHover={onSpaceHover}/>;
                        })}
                        {loading && (
                            <Stack direction='row' sx={{alignContent: 'center', justifyContent: 'center'}}>
                                <CircularProgress/>
                            </Stack>
                        )}
                    </List>
                )}
                {!scrollable && hasMore && (
                    <Stack direction='row' sx={{alignContent: 'center', justifyContent: 'center'}}>
                        <Button variant='contained' size='medium'
                                onClick={() => setPage(page + 1)}
                                sx={{width: '25%', minWidth: '128px'}}
                                disabled={loading}>
                            Load More
                        </Button>
                    </Stack>
                )}
            </Stack>
        </>;
    }
}

export default SpacesList;
